/**
 * RESPONSIBILITY: annotation metadata → Zod. SERVER-SIDE VALIDATION.
 *
 * Derived from the same metadata as the JSON Schema and the form, so what the editor
 * is allowed to type and what the server is willing to store cannot disagree.
 */
import { z } from 'zod';
import { type FieldMeta, type SectionMeta, rowFields } from '../decorators';
import { ROW_ID } from '../decorators/types';
import { repeatProblem } from '../repeat-parse';
import { disallowedDirectives } from '../markdown-directives';

function fieldToZod(f: FieldMeta): z.ZodTypeAny {
  let s: z.ZodTypeAny;

  switch (f.kind) {
    case 'text':
    case 'paragraph': {
      let t = z.string();
      if (f.maxLength) t = t.max(f.maxLength);
      if (f.minLength) t = t.min(f.minLength);
      // The annotation's `allow` list becomes a server-side rule with no app code —
      // the whole point of deriving the validator from the same declaration as the form.
      s = f.markdown
        ? t.refine(
            (v) => disallowedDirectives(v, f.allow ?? []).length === 0,
            (v) => ({ message: `Not allowed here: ${disallowedDirectives(v, f.allow ?? []).map((n) => `:${n}`).join(', ')}` }),
          )
        : t;
      break;
    }
    case 'color':
      s = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Must be a hex colour like #0d4f74');
      break;
    // One shape for both: an uploaded file is a path and the words that go with it,
    // whether those bytes are a photograph or a video. See decorators @Video.
    case 'img':
    case 'video':
      s = z.object({ path: z.string(), alt: z.string().default('') });
      break;
    case 'link':
      s = z.string();
      break;
    // Refused at the boundary, so nothing downstream has to cope with a rule that cannot
    // be read. Empty stays legal — an event with a fixed date has no rule — and so does a
    // rule half chosen, which simply has no next date yet.
    case 'repeat':
      s = z.string().refine(
        (v) => v.trim() === '' || repeatProblem(v) === '',
        (v) => ({ message: `Not a repeat rule: ${repeatProblem(v)}` }),
      );
      break;
    // The control emits what <input type=date|datetime-local> produces, so that is
    // exactly what is accepted. It was `z.string()`, which let "banana" through — and a
    // field nobody validates is a field the page has to defend against at render time.
    // Empty stays legal: an optional date not yet chosen is not an error.
    case 'date':
      s = z.string().regex(
        f.time ? /^$|^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/ : /^$|^\d{4}-\d{2}-\d{2}$/,
        f.time ? 'Must be a date and time, e.g. 2026-08-12T19:30' : 'Must be a date, e.g. 2026-08-12',
      );
      break;
    case 'num': {
      let n = f.integer ? z.number().int() : z.number();
      if (f.min !== undefined) n = n.min(f.min);
      if (f.max !== undefined) n = n.max(f.max);
      s = n;
      break;
    }
    case 'bool':
      s = z.boolean();
      break;
    // With `in`, the allowed values live in the document and are checked on write, so
    // this only guarantees the shape. With fixed options it is still a real enum.
    //
    // EMPTY IS NOT A WRONG ANSWER unless the field is required. A row added and not yet
    // filled in carries "" in every column, so a fixed choice refused the whole save with
    // "Invalid enum value ... received ''" — one blank dropdown in one new row blocking
    // eighty events that were perfectly fine. Nothing downstream needs defending: a theme
    // nobody chose renders nothing, which is what an unfinished row should look like.
    case 'choice': {
      const fixed = !f.in && f.options
        ? z.enum(f.options as [string, ...string[]])
        : undefined;
      const one = fixed
        ? (f.required ? fixed : z.union([fixed, z.literal('')]))
        : z.string();
      s = f.multiple ? z.array(one) : one;
      break;
    }
    case 'stringlist': {
      let a = z.array(z.string());
      if (f.maxItems !== undefined) a = a.max(f.maxItems);
      s = a;
      break;
    }
    case 'list': {
      const itemFields = f.of ? rowFields(f.of()) : [];
      // A row carries its id through the write. z.object() strips what it does not
      // declare, so leaving this out would delete the identity on every save — and
      // every link to that row with it.
      let a = z.array(zodObject(itemFields).extend({ [ROW_ID]: z.string().optional() }));
      if (f.minItems !== undefined) a = a.min(f.minItems);
      if (f.maxItems !== undefined) a = a.max(f.maxItems);
      s = a;
      break;
    }
  }

  return f.required ? s : s.optional();
}

function zodObject(fields: FieldMeta[]): z.ZodObject<z.ZodRawShape> {
  const shape: z.ZodRawShape = {};
  for (const f of fields) shape[f.key] = fieldToZod(f);
  return z.object(shape);
}

export const toZod = (section: SectionMeta): z.ZodObject<z.ZodRawShape> => zodObject(section.fields);
